/**
 * AxLabs Biology Experiments - Ecology
 *
 * Collection of ecology experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, getStateValues } from '../../physics/generated/helpers.js';
// ============================================================================
// COMPETITION (LOTKA-VOLTERRA)
// ============================================================================
export const CompetitionLotkaVolterra = createExperimentFromSchema({
    id: 'competition-lotka-volterra',
    name: 'Competition (Lotka-Volterra)',
    domain: 'biology',
    description: 'Interspecific competition between two species',
    conceptTags: ['Interspecific Competition', 'Lotka-Volterra', 'Competitive Exclusion', 'Coexistence'],
    difficulty: 6,
    stateVariables: [
        { id: 'N1', name: 'Species 1 Population', unit: 'individuals', initial: 10, symbol: 'N_1', description: 'Population size of species 1' },
        { id: 'N2', name: 'Species 2 Population', unit: 'individuals', initial: 10, symbol: 'N_2', description: 'Population size of species 2' },
    ],
    parameters: [
        { id: 'r1', name: 'Species 1 Growth Rate', unit: '1/time', min: 0.01, max: 2, default: 0.5, symbol: 'r_1', description: 'Intrinsic growth rate of species 1' },
        { id: 'r2', name: 'Species 2 Growth Rate', unit: '1/time', min: 0.01, max: 2, default: 0.4, symbol: 'r_2', description: 'Intrinsic growth rate of species 2' },
        { id: 'K1', name: 'Species 1 Carrying Capacity', unit: 'individuals', min: 50, max: 500, default: 100, symbol: 'K_1', description: 'Carrying capacity of species 1' },
        { id: 'K2', name: 'Species 2 Carrying Capacity', unit: 'individuals', min: 50, max: 500, default: 100, symbol: 'K_2', description: 'Carrying capacity of species 2' },
        { id: 'alpha12', name: 'Competition Coeff 1→2', unit: '', min: 0, max: 2, default: 0.5, symbol: '\\alpha_{12}', description: 'Effect of species 1 on species 2' },
        { id: 'alpha21', name: 'Competition Coeff 2→1', unit: '', min: 0, max: 2, default: 0.5, symbol: '\\alpha_{21}', description: 'Effect of species 2 on species 1' },
    ],
    equations: [
        {
            id: 'lotka-volterra-competition',
            name: 'Lotka-Volterra Competition Equations',
            latex: '\\frac{dN_1}{dt} = r_1 N_1 \\left(1 - \\frac{N_1 + \\alpha_{12}N_2}{K_1}\\right)',
            description: 'Species 1 growth with competition',
        },
        {
            id: 'species2-growth',
            name: 'Species 2 Growth',
            latex: '\\frac{dN_2}{dt} = r_2 N_2 \\left(1 - \\frac{N_2 + \\alpha_{21}N_1}{K_2}\\right)',
            description: 'Species 2 growth with competition',
        },
        {
            id: 'coexistence-condition',
            name: 'Coexistence Condition',
            latex: '\\frac{1}{\\alpha_{12}\\alpha_{21}} > 1',
            description: 'Stable coexistence occurs when this holds',
        },
    ],
    derivatives: (state, params) => {
        const N1 = getState(state, 'N1');
        const N2 = getState(state, 'N2');
        const r1 = getParam(params, 'r1');
        const r2 = getParam(params, 'r2');
        const K1 = getParam(params, 'K1');
        const K2 = getParam(params, 'K2');
        const alpha12 = getParam(params, 'alpha12');
        const alpha21 = getParam(params, 'alpha21');
        const dN1_dt = r1 * N1 * (1 - (N1 + alpha12 * N2) / K1);
        const dN2_dt = r2 * N2 * (1 - (N2 + alpha21 * N1) / K2);
        return {
            N1: dN1_dt,
            N2: dN2_dt,
        };
    },
    derivedState: (_state, params) => {
        const K1 = getParam(params, 'K1');
        const K2 = getParam(params, 'K2');
        const alpha12 = getParam(params, 'alpha12');
        const alpha21 = getParam(params, 'alpha21');
        // Coexistence conditions
        const species1_can_invade = K1 > alpha12 * K2;
        const species2_can_invade = K2 > alpha21 * K1;
        const stable_coexistence = species1_can_invade && species2_can_invade;
        // Zero-growth isoclines
        const N1_isocline_at_zero_N2 = K1;
        const N2_isocline_at_zero_N1 = K2;
        return {
            species1_can_invade: species1_can_invade ? 1 : 0,
            species2_can_invade: species2_can_invade ? 1 : 0,
            stable_coexistence: stable_coexistence ? 1 : 0,
            competitive_exclusion: !stable_coexistence ? 1 : 0,
            species1_wins: (K1 > alpha12 * K2 && K2 <= alpha21 * K1) ? 1 : 0,
            species2_wins: (K2 > alpha21 * K1 && K1 <= alpha12 * K2) ? 1 : 0,
            N1_isocline_intercept: N1_isocline_at_zero_N2,
            N2_isocline_intercept: N2_isocline_at_zero_N1,
        };
    },
    failureModes: [
        {
            id: 'negative-population',
            name: 'Negative Population',
            check: (_state, _params) => {
                const N1 = getState(getStateValues(_state), 'N1');
                const N2 = getState(getStateValues(_state), 'N2');
                return N1 < 0 || N2 < 0;
            },
            severity: 'error',
            educationalMessage: 'Population size cannot be negative. A negative value indicates extinction or numerical instability.',
        },
    ],
    bounds: {
        N1: { min: 0, max: 1000 },
        N2: { min: 0, max: 1000 },
    },
});
// ============================================================================
// PREDATOR SATURATION (TYPE II RESPONSE)
// ============================================================================
export const PredatorSaturationTypeII = createExperimentFromSchema({
    id: 'predator-saturation-type2',
    name: 'Predator Saturation (Type II Response)',
    domain: 'biology',
    description: 'Predator with Holling Type II functional response',
    conceptTags: ['Predation', 'Functional Response', 'Handling Time', 'Attack Rate'],
    difficulty: 6,
    stateVariables: [
        { id: 'N', name: 'Prey Population', unit: 'individuals', initial: 50, symbol: 'N', description: 'Prey population size' },
        { id: 'P', name: 'Predator Population', unit: 'individuals', initial: 10, symbol: 'P', description: 'Predator population size' },
    ],
    parameters: [
        { id: 'a', name: 'Attack Rate', unit: '1/time', min: 0.001, max: 1, default: 0.1, symbol: 'a', description: 'Search and attack efficiency' },
        { id: 'h', name: 'Handling Time', unit: 'time', min: 0.01, max: 5, default: 0.5, symbol: 'h', description: 'Time to handle and digest one prey' },
        { id: 'r', name: 'Prey Growth Rate', unit: '1/time', min: 0.01, max: 2, default: 0.5, symbol: 'r', description: 'Prey intrinsic growth rate' },
        { id: 'K', name: 'Prey Carrying Capacity', unit: 'individuals', min: 50, max: 500, default: 200, symbol: 'K' },
        { id: 'e', name: 'Conversion Efficiency', unit: '', min: 0.1, max: 1, default: 0.2, symbol: 'e', description: 'Prey converted to predator offspring' },
        { id: 'd', name: 'Predator Death Rate', unit: '1/time', min: 0.01, max: 0.5, default: 0.1, symbol: 'd', description: 'Predator mortality rate' },
    ],
    equations: [
        {
            id: 'holling-type2',
            name: 'Holling Type II Functional Response',
            latex: 'f(N) = \\frac{aN}{1 + ahN}',
            description: 'Predation rate per predator saturates at high prey density',
        },
        {
            id: 'prey-dynamics',
            name: 'Prey Population Dynamics',
            latex: '\\frac{dN}{dt} = rN\\left(1 - \\frac{N}{K}\\right) - \\frac{aN P}{1 + ahN}',
            description: 'Prey change: logistic growth minus predation',
        },
        {
            id: 'predator-dynamics',
            name: 'Predator Population Dynamics',
            latex: '\\frac{dP}{dt} = \\frac{eaN P}{1 + ahN} - dP',
            description: 'Predator change: energy from prey minus mortality',
        },
    ],
    derivatives: (state, params) => {
        const N = getState(state, 'N');
        const P = getState(state, 'P');
        const a = getParam(params, 'a');
        const h = getParam(params, 'h');
        const r = getParam(params, 'r');
        const K = getParam(params, 'K');
        const e = getParam(params, 'e');
        const d = getParam(params, 'd');
        const functional_response = (a * N) / (1 + a * h * N);
        const predation_rate = functional_response * P;
        const dN_dt = r * N * (1 - N / K) - predation_rate;
        const dP_dt = e * predation_rate - d * P;
        return {
            N: dN_dt,
            P: dP_dt,
        };
    },
    derivedState: (state, params) => {
        const N = getState(state, 'N');
        const P = getState(state, 'P');
        const a = getParam(params, 'a');
        const h = getParam(params, 'h');
        const functional_response = (a * N) / (1 + a * h * N);
        const max_predation_rate = 1 / h;
        const half_saturation = 1 / (a * h);
        const saturation = functional_response / max_predation_rate;
        return {
            functional_response,
            max_predation_rate,
            half_saturation_density: half_saturation,
            saturation_fraction: saturation,
            prey_per_predator: functional_response,
            total_predation: functional_response * P,
        };
    },
    failureModes: [
        {
            id: 'negative-population',
            name: 'Negative Population',
            check: (_state, _params) => {
                const N = getState(getStateValues(_state), 'N');
                const P = getState(getStateValues(_state), 'P');
                return N < 0 || P < 0;
            },
            severity: 'error',
            educationalMessage: 'Population size cannot be negative.',
        },
    ],
    bounds: {
        N: { min: 0, max: 1000 },
        P: { min: 0, max: 500 },
    },
});
// ============================================================================
// PREDATOR SWITCHING (TYPE III RESPONSE)
// ============================================================================
export const PredatorSwitchingTypeIII = createExperimentFromSchema({
    id: 'predator-switching-type3',
    name: 'Predator Switching (Type III Response)',
    domain: 'biology',
    description: 'Predator with Holling Type III sigmoidal functional response',
    conceptTags: ['Predation', 'Type III Response', 'Prey Switching', 'Learning Curve'],
    difficulty: 7,
    stateVariables: [
        { id: 'N', name: 'Prey Population', unit: 'individuals', initial: 30, symbol: 'N', description: 'Prey population size' },
        { id: 'P', name: 'Predator Population', unit: 'individuals', initial: 10, symbol: 'P', description: 'Predator population size' },
    ],
    parameters: [
        { id: 'a', name: 'Maximum Attack Rate', unit: '1/time', min: 0.01, max: 1, default: 0.2, symbol: 'a' },
        { id: 'h', name: 'Handling Time', unit: 'time', min: 0.01, max: 5, default: 0.5, symbol: 'h' },
        { id: 'b', name: 'Half-Saturation Constant', unit: 'individuals', min: 1, max: 100, default: 20, symbol: 'b', description: 'Prey density at half-maximum attack rate' },
        { id: 'r', name: 'Prey Growth Rate', unit: '1/time', min: 0.01, max: 2, default: 0.5, symbol: 'r' },
        { id: 'K', name: 'Prey Carrying Capacity', unit: 'individuals', min: 50, max: 500, default: 200, symbol: 'K' },
        { id: 'e', name: 'Conversion Efficiency', unit: '', min: 0.1, max: 1, default: 0.2, symbol: 'e' },
        { id: 'd', name: 'Predator Death Rate', unit: '1/time', min: 0.01, max: 0.5, default: 0.1, symbol: 'd' },
    ],
    equations: [
        {
            id: 'holling-type3',
            name: 'Holling Type III Functional Response',
            latex: 'f(N) = \\frac{aN^2}{b^2 + N^2 + ahN^2}',
            description: 'Sigmoidal predation rate with learning/switching behavior',
        },
        {
            id: 'prey-dynamics-t3',
            name: 'Prey Dynamics',
            latex: '\\frac{dN}{dt} = rN\\left(1 - \\frac{N}{K}\\right) - \\frac{aN^2 P}{b^2 + N^2(1 + ah)}',
            description: 'Prey change with Type III predation',
        },
        {
            id: 'predator-dynamics-t3',
            name: 'Predator Dynamics',
            latex: '\\frac{dP}{dt} = \\frac{eaN^2 P}{b^2 + N^2(1 + ah)} - dP',
            description: 'Predator change with Type III response',
        },
    ],
    derivatives: (state, params) => {
        const N = getState(state, 'N');
        const P = getState(state, 'P');
        const a = getParam(params, 'a');
        const h = getParam(params, 'h');
        const b = getParam(params, 'b');
        const r = getParam(params, 'r');
        const K = getParam(params, 'K');
        const e = getParam(params, 'e');
        const d = getParam(params, 'd');
        const denominator = b * b + N * N * (1 + a * h);
        const functional_response = (a * N * N) / denominator;
        const predation_rate = functional_response * P;
        const dN_dt = r * N * (1 - N / K) - predation_rate;
        const dP_dt = e * predation_rate - d * P;
        return {
            N: dN_dt,
            P: dP_dt,
        };
    },
    derivedState: (state, params) => {
        const N = getState(state, 'N');
        const a = getParam(params, 'a');
        const b = getParam(params, 'b');
        // At low prey density, Type III gives refuge
        const low_density_refuge = N < b ? 1 : 0;
        const functional_response_approx = (a * N * N) / (b * b + N * N);
        return {
            low_density_refuge,
            functional_response_approx,
            attack_rate_at_half_saturation: a / 2,
            prey_refuge_threshold: b,
        };
    },
    failureModes: [
        {
            id: 'negative-population',
            name: 'Negative Population',
            check: (_state, _params) => {
                const N = getState(getStateValues(_state), 'N');
                const P = getState(getStateValues(_state), 'P');
                return N < 0 || P < 0;
            },
            severity: 'error',
            educationalMessage: 'Population size cannot be negative.',
        },
    ],
    bounds: {
        N: { min: 0, max: 1000 },
        P: { min: 0, max: 500 },
    },
});
// ============================================================================
// SIR EPIDEMIC MODEL
// ============================================================================
export const SIREpidemicModel = createExperimentFromSchema({
    id: 'sir-epidemic-model',
    name: 'SIR Epidemic Model',
    domain: 'biology',
    description: 'Spread of infectious disease through a population',
    conceptTags: ['Epidemiology', 'SIR Model', 'Basic Reproduction Number', 'Herd Immunity'],
    difficulty: 5,
    stateVariables: [
        { id: 'S', name: 'Susceptible', unit: 'individuals', initial: 990, symbol: 'S', description: 'Susceptible population' },
        { id: 'I', name: 'Infected', unit: 'individuals', initial: 10, symbol: 'I', description: 'Infected population' },
        { id: 'R', name: 'Recovered', unit: 'individuals', initial: 0, symbol: 'R', description: 'Recovered/removed population' },
    ],
    parameters: [
        { id: 'beta', name: 'Transmission Rate', unit: '1/time', min: 0.0001, max: 0.01, default: 0.001, symbol: '\\beta', description: 'Contact rate × transmission probability' },
        { id: 'gamma', name: 'Recovery Rate', unit: '1/time', min: 0.01, max: 1, default: 0.1, symbol: '\\gamma', description: 'Rate of recovery (1/infectious period)' },
        { id: 'N', name: 'Total Population', unit: 'individuals', min: 100, max: 100000, default: 1000, symbol: 'N' },
    ],
    equations: [
        {
            id: 'sir-equations',
            name: 'SIR Differential Equations',
            latex: '\\frac{dS}{dt} = -\\beta SI, \\quad \\frac{dI}{dt} = \\beta SI - \\gamma I, \\quad \\frac{dR}{dt} = \\gamma I',
            description: 'Flow between compartments',
        },
        {
            id: 'basic-reproduction-number',
            name: 'Basic Reproduction Number',
            latex: 'R_0 = \\frac{\\beta N}{\\gamma}',
            description: 'Average number of secondary infections from one case',
        },
        {
            id: 'herd-immunity',
            name: 'Herd Immunity Threshold',
            latex: 'HIT = 1 - \\frac{1}{R_0}',
            description: 'Fraction immune needed for herd immunity',
        },
    ],
    derivatives: (state, params) => {
        const S = getState(state, 'S');
        const I = getState(state, 'I');
        const beta = getParam(params, 'beta');
        const gamma = getParam(params, 'gamma');
        const infection_rate = beta * S * I;
        const recovery_rate = gamma * I;
        return {
            S: -infection_rate,
            I: infection_rate - recovery_rate,
            R: recovery_rate,
        };
    },
    derivedState: (state, params) => {
        const S = getState(state, 'S');
        const I = getState(state, 'I');
        const R = getState(state, 'R');
        const beta = getParam(params, 'beta');
        const gamma = getParam(params, 'gamma');
        const N = getParam(params, 'N');
        const R0 = (beta * N) / gamma;
        const herd_immunity_threshold = R0 > 1 ? 1 - 1 / R0 : 0;
        const current_immune_fraction = R / N;
        const susceptible_fraction = S / N;
        const infected_fraction = I / N;
        const recovered_fraction = R / N;
        return {
            R0,
            herd_immunity_threshold,
            current_immune_fraction,
            has_herd_immunity: current_immune_fraction >= herd_immunity_threshold ? 1 : 0,
            epidemic_will_grow: I > 0 && (S / N) > (1 / R0) ? 1 : 0,
            susceptible_fraction,
            infected_fraction,
            recovered_fraction,
            total_population: S + I + R,
        };
    },
    failureModes: [
        {
            id: 'negative-population',
            name: 'Negative Compartment',
            check: (_state, _params) => {
                const S = getState(getStateValues(_state), 'S');
                const I = getState(getStateValues(_state), 'I');
                const R = getState(getStateValues(_state), 'R');
                return S < 0 || I < 0 || R < 0;
            },
            severity: 'error',
            educationalMessage: 'Population compartments cannot be negative.',
        },
        {
            id: 'population-mismatch',
            name: 'Population Conservation Error',
            check: (_state, params) => {
                const S = getState(getStateValues(_state), 'S');
                const I = getState(getStateValues(_state), 'I');
                const R = getState(getStateValues(_state), 'R');
                const N = getParam(params, 'N');
                return Math.abs(S + I + R - N) > 1;
            },
            severity: 'warning',
            educationalMessage: 'The sum of S, I, and R should equal the total population N. There may be a conservation issue.',
        },
    ],
    bounds: {
        S: { min: 0, max: 100000 },
        I: { min: 0, max: 100000 },
        R: { min: 0, max: 100000 },
    },
});
// ============================================================================
// AGE-STRUCTURED POPULATION
// ============================================================================
export const AgeStructuredPopulation = createExperimentFromSchema({
    id: 'age-structured-population',
    name: 'Age-Structured Population',
    domain: 'biology',
    description: 'Population dynamics with age classes (Leslie matrix model)',
    conceptTags: ['Age Structure', 'Leslie Matrix', 'Survival Rate', 'Fecundity'],
    difficulty: 7,
    stateVariables: [
        { id: 'N_juvenile', name: 'Juvenile Population', unit: 'individuals', initial: 100, symbol: 'N_j', description: 'Age class 0-1' },
        { id: 'N_adult', name: 'Adult Population', unit: 'individuals', initial: 50, symbol: 'N_a', description: 'Age class 1+' },
    ],
    parameters: [
        { id: 'F_adult', name: 'Adult Fecundity', unit: 'offspring/adult/time', min: 0, max: 10, default: 2, symbol: 'F_a', description: 'Birth rate per adult' },
        { id: 'S_juvenile', name: 'Juvenile Survival', unit: 'probability', min: 0, max: 1, default: 0.5, symbol: 'S_j', description: 'Survival rate from juvenile to adult' },
        { id: 'S_adult', name: 'Adult Survival', unit: 'probability', min: 0, max: 1, default: 0.8, symbol: 'S_a', description: 'Adult survival rate per time step' },
        { id: 'time_step', name: 'Time Step Duration', unit: 'years', min: 0.1, max: 5, default: 1, symbol: '\\Delta t' },
    ],
    equations: [
        {
            id: 'leslie-matrix',
            name: 'Leslie Matrix Model',
            latex: '\\begin{pmatrix} N_j(t+1) \\\\ N_a(t+1) \\end{pmatrix} = \\begin{pmatrix} 0 & F_a \\\\ S_j & S_a \\end{pmatrix} \\begin{pmatrix} N_j(t) \\\\ N_a(t) \\end{pmatrix}',
            description: 'Discrete-time age-structured model',
        },
        {
            id: 'dominant-eigenvalue',
            name: 'Population Growth Rate',
            latex: '\\lambda = \\frac{S_a + \\sqrt{S_a^2 + 4F_a S_j}}{2}',
            description: 'Asymptotic population growth rate (dominant eigenvalue)',
        },
    ],
    derivatives: (state, params) => {
        const N_j = getState(state, 'N_juvenile');
        const N_a = getState(state, 'N_adult');
        const F_a = getParam(params, 'F_adult');
        const S_j = getParam(params, 'S_juvenile');
        const S_a = getParam(params, 'S_adult');
        // Continuous approximation of discrete model
        // N_j' = F_a * N_a - (mortality) * N_j
        // N_a' = S_j * N_j - (mortality) * N_a
        const juvenile_mortality = 1 - S_j;
        const adult_mortality = 1 - S_a;
        const dN_j_dt = F_a * N_a - juvenile_mortality * N_j;
        const dN_a_dt = S_j * N_j - adult_mortality * N_a;
        return {
            N_juvenile: dN_j_dt,
            N_adult: dN_a_dt,
        };
    },
    derivedState: (_state, params) => {
        const F_a = getParam(params, 'F_adult');
        const S_j = getParam(params, 'S_juvenile');
        const S_a = getParam(params, 'S_adult');
        // Dominant eigenvalue (long-term growth rate)
        const lambda = (S_a + Math.sqrt(S_a * S_a + 4 * F_a * S_j)) / 2;
        // Stable age distribution
        const total_stable = 1 + (lambda - S_a) / S_j;
        const stable_juvenile_fraction = 1 / total_stable;
        const stable_adult_fraction = (lambda - S_a) / S_j / total_stable;
        return {
            lambda,
            population_growing: lambda > 1 ? 1 : 0,
            population_declining: lambda < 1 ? 1 : 0,
            population_stable: Math.abs(lambda - 1) < 0.01 ? 1 : 0,
            stable_juvenile_fraction,
            stable_adult_fraction,
            net_reproductive_rate_R0: F_a * S_a / (1 - S_a),
            generation_time: 1 / (1 - S_a),
        };
    },
    failureModes: [
        {
            id: 'negative-population',
            name: 'Negative Population',
            check: (_state, _params) => {
                const N_j = getState(getStateValues(_state), 'N_juvenile');
                const N_a = getState(getStateValues(_state), 'N_adult');
                return N_j < 0 || N_a < 0;
            },
            severity: 'error',
            educationalMessage: 'Population cannot be negative.',
        },
        {
            id: 'invalid-survival',
            name: 'Invalid Survival Rate',
            check: (_state, params) => {
                const S_j = getParam(params, 'S_juvenile');
                const S_a = getParam(params, 'S_adult');
                return S_j < 0 || S_j > 1 || S_a < 0 || S_a > 1;
            },
            severity: 'error',
            educationalMessage: 'Survival rates must be between 0 and 1.',
        },
    ],
    bounds: {
        N_juvenile: { min: 0, max: 10000 },
        N_adult: { min: 0, max: 10000 },
    },
});
// ============================================================================
// ENERGY FLOW (TROPHIC LEVELS)
// ============================================================================
export const EnergyFlowTrophicLevels = createExperimentFromSchema({
    id: 'energy-flow-trophic-levels',
    name: 'Energy Flow (Trophic Levels)',
    domain: 'biology',
    description: 'Energy transfer between trophic levels in an ecosystem',
    conceptTags: ['Trophic Levels', 'Energy Transfer', 'Ecological Efficiency', 'Food Chain'],
    difficulty: 5,
    stateVariables: [
        { id: 'E_producer', name: 'Producer Energy', unit: 'kcal/m²/year', initial: 10000, symbol: 'E_1', description: 'Primary production (NPP)' },
    ],
    parameters: [
        { id: 'TE', name: 'Trophic Efficiency', unit: '%', min: 1, max: 30, default: 10, symbol: 'TE', description: 'Energy transfer efficiency between levels (typically 10%)' },
        { id: 'num_levels', name: 'Number of Trophic Levels', unit: '', min: 2, max: 6, default: 4, symbol: 'n', description: 'Number of trophic levels to model' },
    ],
    equations: [
        {
            id: 'energy-transfer',
            name: 'Energy Transfer',
            latex: 'E_{n+1} = \\frac{TE}{100} \\times E_n',
            description: 'Energy available at next trophic level',
        },
        {
            id: 'cumulative-efficiency',
            name: 'Cumulative Efficiency',
            latex: 'E_n = E_1 \\left(\\frac{TE}{100}\\right)^{n-1}',
            description: 'Energy at level n from primary production',
        },
        {
            id: 'energy-pyramid',
            name: 'Energy Pyramid',
            latex: 'E_1 > E_2 > E_3 > \\cdots > E_n',
            description: 'Energy decreases at each trophic level',
        },
    ],
    derivatives: (_state, _params) => ({
        E_producer: 0,
    }),
    derivedState: (state, params) => {
        const E_1 = getState(state, 'E_producer');
        const TE = getParam(params, 'TE');
        const num_levels = getParam(params, 'num_levels');
        const efficiency = TE / 100;
        // Calculate energies at different levels (up to 6 levels max for return values)
        const E_level1 = E_1;
        const E_level2 = E_1 * Math.pow(efficiency, 1);
        const E_level3 = E_1 * Math.pow(efficiency, 2);
        const E_level4 = E_1 * Math.pow(efficiency, 3);
        const E_level5 = E_1 * Math.pow(efficiency, 4);
        const E_level6 = E_1 * Math.pow(efficiency, 5);
        // Energy at the top level (last level)
        const E_top = num_levels >= 2 ? E_1 * Math.pow(efficiency, num_levels - 1) : E_1;
        // Cumulative losses at each level
        const cumulative_loss_level1 = 0;
        const cumulative_loss_level2 = E_1 - E_level2;
        const cumulative_loss_level3 = E_1 - E_level3;
        const cumulative_loss_level4 = E_1 - E_level4;
        // Losses between levels
        const loss_1_to_2 = E_level1 - E_level2;
        const loss_2_to_3 = E_level2 - E_level3;
        const loss_3_to_4 = E_level3 - E_level4;
        // Total calculations
        const total_loss = E_1 - E_top;
        const total_energy_in_system = E_top * (1 - Math.pow(efficiency, num_levels)) / (1 - efficiency) / efficiency * E_1;
        return {
            // Energy at each level
            energy_level1: E_level1,
            energy_level2: E_level2,
            energy_level3: E_level3,
            energy_level4: E_level4,
            energy_level5: E_level5,
            energy_level6: E_level6,
            energy_top: E_top,
            // Losses between consecutive levels
            loss_1_to_2,
            loss_2_to_3,
            loss_3_to_4,
            // Cumulative losses
            cumulative_loss_level1,
            cumulative_loss_level2,
            cumulative_loss_level3,
            cumulative_loss_level4,
            // Totals
            total_loss,
            total_energy_in_system,
            // Percentages
            percent_energy_remaining_top: (E_top / E_1) * 100,
            cumulative_efficiency: Math.pow(efficiency, num_levels - 1),
        };
    },
    failureModes: [
        {
            id: 'negative-energy',
            name: 'Negative Energy',
            check: (_state, _params) => {
                const E_producer = getState(getStateValues(_state), 'E_producer');
                return E_producer < 0;
            },
            severity: 'error',
            educationalMessage: 'Energy cannot be negative.',
        },
        {
            id: 'excessive-efficiency',
            name: 'Excessive Trophic Efficiency',
            check: (_state, params) => {
                const TE = getParam(params, 'TE');
                return TE > 25;
            },
            severity: 'warning',
            educationalMessage: 'Trophic efficiency > 25% is unusually high for natural ecosystems. Typical values are 5-15%.',
        },
    ],
    bounds: {
        E_producer: { min: 0, max: 100000 },
    },
});
//# sourceMappingURL=ecology.js.map